import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Loader2,
  Paperclip,
  Music,
  Video,
  Image as ImageIcon,
  FileText,
  X,
  Save,
} from "lucide-react";
import { toast } from "sonner";
import { uploadToR2, adminCreatePerk, adminUpdatePerk } from "../../lib/studioApi";

const MAX_SIZE_BY_KIND = {
  audio: 100 * 1024 * 1024,
  video: 500 * 1024 * 1024,
  image: 20 * 1024 * 1024,
  file: 100 * 1024 * 1024,
};

function detectKind(file) {
  const t = (file.type || "").toLowerCase();
  if (t.startsWith("audio/")) return "audio";
  if (t.startsWith("video/")) return "video";
  if (t.startsWith("image/")) return "image";
  return "file";
}

function KindIcon({ kind }) {
  if (kind === "audio") return <Music className="w-4 h-4" />;
  if (kind === "video") return <Video className="w-4 h-4" />;
  if (kind === "image") return <ImageIcon className="w-4 h-4" />;
  return <FileText className="w-4 h-4" />;
}

const makePendingId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

/**
 * PerkUploadDialog — modal for creating or editing a perk.
 * Handles main attachments (video/audio/PDF/ZIP) AND a separate thumbnail image upload.
 * Closes on save. Calls `onSaved(perk)` so the caller can refresh the list.
 */
export default function PerkUploadDialog({
  open,
  onOpenChange,
  perk,           // null when creating, the perk object when editing
  categories,
  onSaved,
}) {
  const isEdit = Boolean(perk?.id);

  const [categoryId, setCategoryId] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [featured, setFeatured] = useState(false);
  const [sendEmail, setSendEmail] = useState(false);
  const [thumbnailUrl, setThumbnailUrl] = useState("");
  const [externalUrl, setExternalUrl] = useState("");
  const [thumbUploading, setThumbUploading] = useState(false);
  const [attachments, setAttachments] = useState([]); // saved attachment objects
  const [pending, setPending] = useState([]);         // in-flight uploads
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);
  const thumbInputRef = useRef(null);

  // Hydrate when editing / reset when creating.
  useEffect(() => {
    if (!open) return;
    if (isEdit) {
      setCategoryId(perk.categoryId || "");
      setTitle(perk.title || "");
      setDescription(perk.description || "");
      setFeatured(Boolean(perk.featured));
      setThumbnailUrl(perk.thumbnailUrl || "");
      setExternalUrl(perk.externalUrl || "");
      setAttachments(perk.attachments || []);
      setSendEmail(false);
    } else {
      const firstNonHidden = (categories || []).find((c) => !c.hidden);
      setCategoryId(firstNonHidden ? firstNonHidden.id : "");
      setTitle("");
      setDescription("");
      setFeatured(false);
      setThumbnailUrl("");
      setExternalUrl("");
      setAttachments([]);
      setSendEmail(false);
    }
    setPending([]);
  }, [open, isEdit, perk, categories]);

  const visibleCategories = (categories || []).filter((c) => !c.hidden);

  const addFiles = useCallback((fileList) => {
    const files = Array.from(fileList || []);
    files.forEach((file) => {
      const kind = detectKind(file);
      const cap = MAX_SIZE_BY_KIND[kind] || MAX_SIZE_BY_KIND.file;
      if (file.size > cap) {
        toast.error(`${file.name} exceeds the ${Math.round(cap / (1024 * 1024))}MB limit`);
        return;
      }
      const id = makePendingId();
      setPending((prev) => [...prev, { id, file, kind, progress: 0 }]);
      uploadToR2(file, kind, {
        asAdmin: true,
        onProgress: (pct) => {
          setPending((prev) => prev.map((p) => (p.id === id ? { ...p, progress: pct } : p)));
        },
      })
        .then((attachment) => {
          setPending((prev) => prev.filter((p) => p.id !== id));
          setAttachments((prev) => [...prev, { ...attachment, kind }]);
        })
        .catch((err) => {
          toast.error(`Upload failed: ${file.name}`);
          console.error(err);
          setPending((prev) => prev.filter((p) => p.id !== id));
        });
    });
  }, []);

  const removeAttachment = (idx) => {
    setAttachments((prev) => prev.filter((_, i) => i !== idx));
  };
  const removePending = (id) => {
    setPending((prev) => prev.filter((p) => p.id !== id));
  };

  const onPickThumb = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Thumbnail must be an image (JPG, PNG, WebP)");
      return;
    }
    if (file.size > MAX_SIZE_BY_KIND.image) {
      toast.error("Thumbnail too large (max 20MB)");
      return;
    }
    setThumbUploading(true);
    try {
      const att = await uploadToR2(file, "image", { asAdmin: true });
      setThumbnailUrl(att.url);
    } catch (err) {
      toast.error("Thumbnail upload failed");
      console.error(err);
    } finally {
      setThumbUploading(false);
    }
  };

  const save = async () => {
    if (!categoryId) {
      toast.error("Pick a category");
      return;
    }
    if (!title.trim()) {
      toast.error("Add a title");
      return;
    }
    if (pending.length > 0) {
      toast.error("Wait for uploads to finish");
      return;
    }
    const trimmedExternal = externalUrl.trim();
    if (trimmedExternal && !/^https?:\/\//i.test(trimmedExternal)) {
      toast.error("External link must start with http:// or https://");
      return;
    }
    if (attachments.length === 0 && !trimmedExternal) {
      toast.error("Add at least one file or an external link");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        categoryId,
        title: title.trim(),
        description: description.trim(),
        attachments: attachments.map((a) => ({
          kind: a.kind,
          url: a.url,
          name: a.name || "",
          size: a.size || 0,
          contentType: a.contentType || "",
        })),
        thumbnailUrl: thumbnailUrl.trim(),
        externalUrl: trimmedExternal,
        featured,
      };
      let saved;
      if (isEdit) {
        saved = await adminUpdatePerk(perk.id, payload);
      } else {
        saved = await adminCreatePerk({ ...payload, targetUserIds: [], sendEmail });
      }
      onSaved?.(saved);
      onOpenChange(false);
      toast.success(
        isEdit
          ? "Perk updated"
          : sendEmail && saved.emailedCount
            ? `Perk created. Emailed ${saved.emailedCount} artist${saved.emailedCount === 1 ? "" : "s"}.`
            : "Perk created",
      );
    } catch (err) {
      const msg = err?.response?.data?.detail || (isEdit ? "Could not update perk" : "Could not create perk");
      toast.error(typeof msg === "string" ? msg : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="perk-upload-dialog"
        className="max-w-xl bg-[#10162a] border-white/10 text-white max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="text-xl font-extrabold text-white">
            {isEdit ? "Edit perk" : "Add a new perk"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          {/* Category */}
          <div>
            <Label className="text-xs font-semibold text-white/50 uppercase tracking-wider">Category</Label>
            <Select value={categoryId} onValueChange={setCategoryId}>
              <SelectTrigger
                data-testid="perk-category-select"
                className="mt-1 bg-[#0b0f1e] border-white/10 text-white focus:ring-paul-yellow/40"
              >
                <SelectValue placeholder="Choose category" />
              </SelectTrigger>
              <SelectContent>
                {visibleCategories.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Title */}
          <div>
            <Label className="text-xs font-semibold text-white/50 uppercase tracking-wider">Title</Label>
            <Input
              data-testid="perk-title-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Songwriting Blueprint"
              className="mt-1 bg-[#0b0f1e] border-white/10 text-white placeholder:text-white/30 focus-visible:border-paul-yellow/60 focus-visible:ring-paul-yellow/40"
            />
          </div>

          {/* Description */}
          <div>
            <Label className="text-xs font-semibold text-white/50 uppercase tracking-wider">Description</Label>
            <Textarea
              data-testid="perk-description-input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Short description shown on the card and the in-app player."
              rows={3}
              className="mt-1 bg-[#0b0f1e] border-white/10 text-white placeholder:text-white/30 focus-visible:border-paul-yellow/60 focus-visible:ring-paul-yellow/40 resize-none"
            />
          </div>

          {/* Thumbnail */}
          <div>
            <Label className="text-xs font-semibold text-white/50 uppercase tracking-wider">
              Thumbnail (optional)
            </Label>
            <div className="mt-1 flex items-center gap-3">
              <div className="relative w-32 h-20 rounded-lg overflow-hidden border border-white/10 bg-[#0b0f1e] flex items-center justify-center text-white/30">
                {thumbnailUrl ? (
                  <img src={thumbnailUrl} alt="thumbnail" className="w-full h-full object-cover" />
                ) : (
                  <ImageIcon className="w-5 h-5" />
                )}
              </div>
              <input
                ref={thumbInputRef}
                type="file"
                accept="image/*"
                hidden
                onChange={onPickThumb}
                data-testid="perk-thumb-file"
              />
              <div className="flex flex-col gap-1.5">
                <Button
                  type="button"
                  onClick={() => thumbInputRef.current?.click()}
                  disabled={thumbUploading}
                  variant="outline"
                  className="border-white/15 bg-transparent text-white/80 hover:bg-white/10 hover:text-white gap-2 h-9"
                  data-testid="perk-thumb-upload-btn"
                >
                  {thumbUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />}
                  {thumbnailUrl ? "Change" : "Upload image"}
                </Button>
                {thumbnailUrl && (
                  <button
                    type="button"
                    onClick={() => setThumbnailUrl("")}
                    className="text-[11px] text-white/40 hover:text-red-300 text-left"
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* External Link — optional resource URL (Google Drive, YouTube, custom tool, etc.). */}
          <div>
            <Label className="text-xs font-semibold text-white/50 uppercase tracking-wider">
              External Link (optional)
            </Label>
            <Input
              data-testid="perk-external-url-input"
              value={externalUrl}
              onChange={(e) => setExternalUrl(e.target.value)}
              placeholder="https://chord-generator.paulvilas.com"
              type="url"
              autoComplete="off"
              spellCheck="false"
              className="mt-1 bg-[#0b0f1e] border-white/10 text-white placeholder:text-white/30 focus-visible:border-paul-yellow/60 focus-visible:ring-paul-yellow/40"
            />
            <p className="text-[11px] text-white/40 mt-1.5">
              Link out to an external tool, generator, Drive folder, or YouTube — opens in a new tab from the perk card.
            </p>
          </div>

          {/* Attachments */}
          <div>
            <Label className="text-xs font-semibold text-white/50 uppercase tracking-wider">
              Files (optional if an external link is set)
            </Label>
            <div className="mt-2 space-y-2">
              {attachments.map((a, idx) => (
                <div
                  key={`a-${idx}-${a.url}`}
                  className="flex items-center gap-3 bg-[#161a2c] rounded-lg p-2.5 border border-white/5"
                >
                  <div className="w-8 h-8 rounded-md bg-paul-yellow/15 text-paul-yellow flex items-center justify-center flex-shrink-0">
                    <KindIcon kind={a.kind} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-white truncate">{a.name}</div>
                    <div className="text-[11px] text-white/40">
                      {a.kind} · {a.size ? `${Math.round(a.size / 1024)} KB` : "—"}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeAttachment(idx)}
                    className="text-white/40 hover:text-red-400"
                    aria-label="Remove attachment"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
              {pending.map((p) => (
                <div
                  key={p.id}
                  className="flex items-center gap-3 bg-[#161a2c] rounded-lg p-2.5 border border-white/5"
                >
                  <div className="w-8 h-8 rounded-md bg-paul-yellow/15 text-paul-yellow flex items-center justify-center flex-shrink-0">
                    <KindIcon kind={p.kind} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-white truncate">{p.file.name}</div>
                    <div className="h-1.5 bg-white/10 rounded-full mt-1 overflow-hidden">
                      <div className="h-full bg-paul-yellow transition-all" style={{ width: `${p.progress}%` }} />
                    </div>
                  </div>
                  <span className="text-[11px] text-white/50 font-mono min-w-[35px] text-right">{p.progress}%</span>
                  <button
                    type="button"
                    onClick={() => removePending(p.id)}
                    className="text-white/40 hover:text-red-400"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>

            <Button
              type="button"
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              className="mt-3 border-white/15 bg-transparent text-white/80 hover:bg-white/10 hover:text-white gap-2"
              data-testid="perk-attach-btn"
            >
              <Paperclip className="w-4 h-4" />
              Attach files
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              hidden
              onChange={(e) => {
                if (e.target.files) addFiles(e.target.files);
              }}
            />
            <p className="text-[11px] text-white/40 mt-1.5">
              Video ≤ 500MB · Audio ≤ 100MB · Image ≤ 20MB · Other ≤ 100MB
            </p>
          </div>

          {/* Featured + Email */}
          <div className="space-y-3 pt-2 border-t border-white/5">
            <label className="flex items-start gap-2.5 cursor-pointer select-none" data-testid="perk-featured-toggle">
              <Checkbox
                checked={featured}
                onCheckedChange={(v) => setFeatured(Boolean(v))}
                className="mt-0.5 border-white/30 data-[state=checked]:bg-paul-yellow data-[state=checked]:text-navy data-[state=checked]:border-paul-yellow"
              />
              <span className="text-sm text-white/80">
                Featured
                <span className="block text-[11px] text-white/40 font-normal">
                  Pinned to the top of the artist library and sorted first.
                </span>
              </span>
            </label>

            {!isEdit && (
              <label className="flex items-start gap-2.5 cursor-pointer select-none" data-testid="perk-email-toggle">
                <Checkbox
                  checked={sendEmail}
                  onCheckedChange={(v) => setSendEmail(Boolean(v))}
                  className="mt-0.5 border-white/30 data-[state=checked]:bg-paul-yellow data-[state=checked]:text-navy data-[state=checked]:border-paul-yellow"
                />
                <span className="text-sm text-white/80">
                  Email everyone the announcement
                  <span className="block text-[11px] text-white/40 font-normal">
                    Sends &ldquo;Paul added a new {visibleCategories.find((c) => c.id === categoryId)?.name || "Perk"}: {title || "…"}&rdquo;
                  </span>
                </span>
              </label>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
            className="border-white/15 bg-transparent text-white/80 hover:bg-white/10 hover:text-white"
          >
            Cancel
          </Button>
          <Button
            type="button"
            data-testid="perk-save-btn"
            onClick={save}
            disabled={saving || pending.length > 0}
            className="bg-paul-yellow hover:brightness-105 text-navy font-bold gap-2"
            style={{ color: "#0b1535" }}
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {isEdit ? "Save changes" : "Publish perk"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
